/**
 * TanStack-Query roster hook. Single source of truth for the Manage Crew
 * grid in zitadel mode. Joins:
 *
 *   - /v2/users (the user records)
 *   - /management/v1/users/grants/_search (their XAES project roles)
 *
 * …into the existing `UserProfile` shape so the rest of the SPA keeps
 * consuming `useAuth().users` without caring whether the data came from
 * ZITADEL or the seed roster.
 *
 * Mutations are exposed alongside the query. They call the management
 * API and invalidate the roster query on success so the grid refreshes
 * without a page reload.
 */

import { useCallback, useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { env } from "@/env";
import {
  createHumanUser,
  deactivateUser,
  listAllUserGrants,
  listUsers,
  reactivateUser,
  setProjectRolesForUser,
  setUserPassword,
  updateHumanUser,
  type ZitadelUserSummary,
} from "@/features/auth/management-api";
import {
  roleDisplayFromKey,
  roleKeyFromDisplay,
  ROLE_OBSERVER,
} from "@/features/auth/roles";
import type { UserProfile } from "@/content/users";

const ROSTER_QUERY_KEY = ["zitadel", "roster"] as const;

interface RosterEntry extends UserProfile {
  /** Stable Zitadel user ID — mutations route by this. */
  userId: string;
  /** Entra JIT users gated by the approval Action land inactive first. */
  pendingApproval?: boolean;
}

function toUserProfile(
  user: ZitadelUserSummary,
  roleKeys: string[],
): RosterEntry {
  const profile = user.human?.profile;
  const display =
    profile?.displayName ??
    [profile?.givenName, profile?.familyName].filter(Boolean).join(" ") ??
    user.username;
  // Pick the highest-priority role key the user has on the XAES project;
  // fall back to OBSERVER so the UI always has something to render.
  const firstMappedKey = roleKeys
    .map((k) => roleDisplayFromKey(k))
    .find((d): d is string => d !== null);
  return {
    userId: user.userId,
    username: user.username,
    password: "",
    displayName: display || user.username,
    role: firstMappedKey ?? ROLE_OBSERVER,
  };
}

export function useRoster({ enabled = true }: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ROSTER_QUERY_KEY,
    // Off until signed in: without a token every call fails locally and the
    // default retry/backoff burns ~7s of /zitadel noise during sign-in.
    enabled,
    queryFn: async (): Promise<RosterEntry[]> => {
      // Two independent calls in parallel — users + their grants.
      const [users, grants] = await Promise.all([
        listUsers(),
        listAllUserGrants().catch(() => []), // grants may 403 if facilitator
        //                                       lacks ORG_USER_MANAGER; treat
        //                                       as "no role data" rather
        //                                       than failing the whole roster
      ]);
      const grantsByUser = new Map<string, string[]>();
      for (const g of grants) {
        if (
          env.VITE_ZITADEL_PROJECT_ID &&
          g.projectId !== env.VITE_ZITADEL_PROJECT_ID
        )
          continue;
        const existing = grantsByUser.get(g.userId) ?? [];
        grantsByUser.set(g.userId, existing.concat(g.roleKeys));
      }
      // Soft-deleted / Entra-JIT-gated users land as USER_STATE_INACTIVE.
      // Surface them as pendingApproval so Manage Crew can Approve
      // (reactivate). Only hard-deleted users are hidden from the grid.
      const HIDDEN_STATES = new Set(["USER_STATE_DELETED"]);
      const PENDING_STATES = new Set(["USER_STATE_INACTIVE"]);
      return users
        .filter((u) => !u.state || !HIDDEN_STATES.has(u.state))
        .map((u) => ({
          ...toUserProfile(u, grantsByUser.get(u.userId) ?? []),
          pendingApproval: Boolean(u.state && PENDING_STATES.has(u.state)),
        }));
    },
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  const invalidate = useCallback(() => {
    // First refetch catches fast-projection cases (role updates, removes
    // — these reflect within ~100ms).
    void queryClient.invalidateQueries({ queryKey: ROSTER_QUERY_KEY });
    // ZITADEL uses event sourcing — the create/grant calls return as soon
    // as the events are persisted, but the search projection that powers
    // /v2/users + /management/v1/users/grants/_search runs asynchronously
    // and typically lags 500-1500 ms behind. A second refetch at 1500 ms
    // covers the add-user case where the new row isn't visible yet on the
    // first refetch.
    setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ROSTER_QUERY_KEY });
    }, 1500);
  }, [queryClient]);

  /* ── Mutations ───────────────────────────────────────────────────── */

  const resolveUserId = useCallback(
    (username: string): string | null => {
      return (
        query.data?.find((u) => u.username === username)?.userId ?? null
      );
    },
    [query.data],
  );

  // Optimistic-update helpers. Every cache-mutating mutation cancels
  // in-flight refetches, snapshots the prior list, writes the predicted
  // next state, and rolls back on error. onSuccess still triggers
  // `invalidate()` so the deferred refetch confirms the server state.
  const snapshotAndCancel = useCallback(async (): Promise<
    RosterEntry[] | undefined
  > => {
    await queryClient.cancelQueries({ queryKey: ROSTER_QUERY_KEY });
    return queryClient.getQueryData<RosterEntry[]>(ROSTER_QUERY_KEY);
  }, [queryClient]);

  const rollback = useCallback(
    (snapshot: RosterEntry[] | undefined) => {
      queryClient.setQueryData<RosterEntry[] | undefined>(
        ROSTER_QUERY_KEY,
        snapshot,
      );
    },
    [queryClient],
  );

  const addUser = useMutation({
    mutationFn: async (input: {
      username: string;
      displayName: string;
      password: string;
      role: string; // display string
    }) => {
      const [first, ...rest] = input.displayName.split(/\s+/);
      const email = `${input.username}@${env.VITE_ZITADEL_EMAIL_DOMAIN}`;
      const created = await createHumanUser({
        username: input.username,
        email,
        givenName: first ?? input.displayName,
        familyName: rest.join(" ") || input.displayName,
        password: input.password,
        passwordChangeRequired: false,
        emailVerified: true,
      });
      const roleKey = roleKeyFromDisplay(input.role);
      if (roleKey && env.VITE_ZITADEL_PROJECT_ID) {
        await setProjectRolesForUser(created.userId, [roleKey]);
      }
      return created.userId;
    },
    onMutate: async (input) => {
      const snapshot = await snapshotAndCancel();
      // Synthetic userId — gets replaced by the real one on the deferred
      // refetch ~1500ms later. Prefix lets debugging tell them apart.
      const optimistic: RosterEntry = {
        userId: `optimistic-${Date.now()}`,
        username: input.username,
        password: "",
        displayName: input.displayName,
        role: input.role,
      };
      queryClient.setQueryData<RosterEntry[] | undefined>(
        ROSTER_QUERY_KEY,
        (prev) => (prev ? [...prev, optimistic] : [optimistic]),
      );
      return { snapshot };
    },
    onError: (_err, _input, ctx) => rollback(ctx?.snapshot),
    onSuccess: invalidate,
  });

  const approveUser = useMutation({
    mutationFn: async (username: string) => {
      const id = resolveUserId(username);
      if (!id) throw new Error(`User not found: ${username}`);
      await reactivateUser(id);
    },
    onMutate: async (username) => {
      const snapshot = await snapshotAndCancel();
      queryClient.setQueryData<RosterEntry[] | undefined>(
        ROSTER_QUERY_KEY,
        (prev) =>
          prev?.map((u) =>
            u.username === username
              ? { ...u, pendingApproval: false }
              : u,
          ),
      );
      return { snapshot };
    },
    onError: (_err, _input, ctx) => rollback(ctx?.snapshot),
    onSuccess: invalidate,
  });

  const removeUser = useMutation({
    mutationFn: async (username: string) => {
      const id = resolveUserId(username);
      if (!id) throw new Error(`User not found: ${username}`);
      await deactivateUser(id);
    },
    onMutate: async (username) => {
      const snapshot = await snapshotAndCancel();
      queryClient.setQueryData<RosterEntry[] | undefined>(
        ROSTER_QUERY_KEY,
        (prev) => prev?.filter((u) => u.username !== username),
      );
      return { snapshot };
    },
    onError: (_err, _input, ctx) => rollback(ctx?.snapshot),
    onSuccess: invalidate,
  });

  const updateUserRole = useMutation({
    mutationFn: async ({
      username,
      newRole,
    }: {
      username: string;
      newRole: string;
    }) => {
      const id = resolveUserId(username);
      if (!id) throw new Error(`User not found: ${username}`);
      const key = roleKeyFromDisplay(newRole);
      if (!key) throw new Error(`Unknown role: ${newRole}`);
      await setProjectRolesForUser(id, [key]);
    },
    onMutate: async ({ username, newRole }) => {
      const snapshot = await snapshotAndCancel();
      queryClient.setQueryData<RosterEntry[] | undefined>(
        ROSTER_QUERY_KEY,
        (prev) =>
          prev?.map((u) =>
            u.username === username ? { ...u, role: newRole } : u,
          ),
      );
      return { snapshot };
    },
    onError: (_err, _input, ctx) => rollback(ctx?.snapshot),
    onSuccess: invalidate,
  });

  const resetPassword = useMutation({
    mutationFn: async ({
      username,
      newPassword,
    }: {
      username: string;
      newPassword: string;
    }) => {
      const id = resolveUserId(username);
      if (!id) throw new Error(`User not found: ${username}`);
      await setUserPassword(id, newPassword, false);
    },
    // No optimistic branch — password isn't surfaced in the cache shape,
    // so there's nothing visible to predict.
    onSuccess: invalidate,
  });

  const updateUserDetails = useMutation({
    mutationFn: async ({
      username,
      updates,
    }: {
      username: string;
      updates: { displayName?: string; username?: string };
    }) => {
      const id = resolveUserId(username);
      if (!id) throw new Error(`User not found: ${username}`);
      const patch: {
        username?: string;
        givenName?: string;
        familyName?: string;
        displayName?: string;
      } = {};
      if (updates.username !== undefined) patch.username = updates.username;
      if (updates.displayName !== undefined) {
        const [first, ...rest] = updates.displayName.split(/\s+/);
        patch.givenName = first ?? updates.displayName;
        patch.familyName = rest.join(" ") || updates.displayName;
        // ZITADEL stores `displayName` independently and won't recompute
        // it from given/family. Push the whole string explicitly so the
        // value the facilitator typed is what shows up after refetch.
        patch.displayName = updates.displayName;
      }
      await updateHumanUser(id, patch);
    },
    onMutate: async ({ username, updates }) => {
      const snapshot = await snapshotAndCancel();
      queryClient.setQueryData<RosterEntry[] | undefined>(
        ROSTER_QUERY_KEY,
        (prev) =>
          prev?.map((u) =>
            u.username === username
              ? {
                  ...u,
                  ...(updates.displayName !== undefined
                    ? { displayName: updates.displayName }
                    : {}),
                  ...(updates.username !== undefined
                    ? { username: updates.username }
                    : {}),
                }
              : u,
          ),
      );
      return { snapshot };
    },
    onError: (_err, _input, ctx) => rollback(ctx?.snapshot),
    onSuccess: invalidate,
  });

  /* ── Public shape ───────────────────────────────────────────────── */

  const roster = query.data ?? [];
  const activeUsers = useMemo(
    () => roster.filter((u) => !u.pendingApproval),
    [roster],
  );
  const pendingUsers = useMemo(
    () => roster.filter((u) => u.pendingApproval),
    [roster],
  );

  return useMemo(
    () => ({
      users: activeUsers,
      pendingUsers,
      isLoading: query.isLoading,
      error: query.error,
      addUser,
      approveUser,
      removeUser,
      updateUserRole,
      resetPassword,
      updateUserDetails,
    }),
    [
      activeUsers,
      pendingUsers,
      query.isLoading,
      query.error,
      addUser,
      approveUser,
      removeUser,
      updateUserRole,
      resetPassword,
      updateUserDetails,
    ],
  );
}

export type Roster = ReturnType<typeof useRoster>;
